import { SetupAnalysisResult } from '@/types/agent/setup';

export interface StepValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export const validateStep = (
  stepId: string,
  analysis: SetupAnalysisResult
): StepValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  switch (stepId) {
    case 'branding':
      // Branding step validation
      if (!analysis.branding?.customAvatarUrl) {
        warnings.push('No custom avatar selected - using default');
      }

      if (!analysis.branding?.primaryColor) {
        warnings.push('No custom primary color - using default brand color');
      }

      // Branding is optional, so no hard errors
      break;

    case 'identity':
      // Required fields
      if (!analysis.genieName?.trim()) {
        errors.push('Genie name is required');
      } else if (analysis.genieName.length < 3) {
        errors.push('Genie name must be at least 3 characters');
      } else if (analysis.genieName.length > 50) {
        errors.push('Genie name must be less than 50 characters');
      }

      if (!analysis.category) {
        errors.push('Category is required');
      }

      // Warnings
      if (!analysis.useCase?.trim()) {
        warnings.push('Adding a use case helps users understand your genie better');
      }
      break;

    case 'conversation':
      // Voice validation - now in conversation step
      if (!analysis.voiceId) {
        errors.push('Voice selection is required');
      }

      // First message validation
      if (!analysis.firstMessage?.trim()) {
        errors.push('First message is required');
      } else {
        const wordCount = analysis.firstMessage.split(/\s+/).filter(Boolean).length;

        if (wordCount < 3) {
          errors.push('First message is too short (minimum 3 words)');
        } else if (wordCount > 100) {
          warnings.push('First message is long - consider shortening for better engagement');
        } else if (wordCount < 10) {
          warnings.push('Consider adding more detail to your first message (10-30 words ideal)');
        }

        // Check if first message includes genie name
        if (!analysis.firstMessage.toLowerCase().includes(analysis.genieName?.toLowerCase() || '')) {
          warnings.push('Consider including your genie name in the first message');
        }
      }

      // System prompt warnings
      if (!analysis.systemPrompt?.trim()) {
        warnings.push('System prompt is empty - using default behavior');
      }
      break;

    case 'insights': {
      const reportMode = analysis.reportMode ?? (analysis.sendConversationReports ? 'every_conversation' : 'off');
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      // Email validation (every_conversation / digest recipients)
      if ((reportMode === 'every_conversation' || reportMode === 'digest') && analysis.supportEmails && analysis.supportEmails.length > 0) {
        const invalidEmails = analysis.supportEmails.filter(email => !emailRegex.test(email));

        if (invalidEmails.length > 0) {
          errors.push(`Invalid email addresses: ${invalidEmails.join(', ')}`);
        }
      }

      if (reportMode === 'every_conversation' && (!analysis.supportEmails || analysis.supportEmails.length === 0)) {
        warnings.push('Email reports enabled but no email addresses configured');
      }

      if (reportMode === 'digest' && !analysis.reportDigestFrequency) {
        errors.push('Digest frequency is required when reporting mode is set to digest');
      }

      if (reportMode === 'escalation_only') {
        const channels = analysis.escalationSettings?.channels ?? [];
        const recipientEmails = analysis.escalationSettings?.recipients?.emails ?? [];
        if (channels.length === 0) {
          errors.push('Select at least one escalation alert channel (email or Slack)');
        }
        if (channels.includes('email')) {
          if (recipientEmails.length === 0) {
            errors.push('At least one escalation recipient email is required');
          } else {
            const invalidEmails = recipientEmails.filter(email => !emailRegex.test(email));
            if (invalidEmails.length > 0) {
              errors.push(`Invalid escalation recipient emails: ${invalidEmails.join(', ')}`);
            }
          }
        }
      }

      // Goal warnings
      const goalString = typeof analysis.goal === 'string' ? analysis.goal : '';
      if (!goalString.trim()) {
        warnings.push('Define a collection goal to help your genie gather relevant information');
      }
      break;
    }

    case 'knowledge':
      // Knowledge base is optional, just warnings
      if (!analysis.knowledgeBase || analysis.knowledgeBase.length === 0) {
        warnings.push('No knowledge base documents selected - genie will use default knowledge only');
      }
      break;

    case 'advanced':
      // Validation for advanced settings
      const maxDuration = analysis.conversationSettings?.maxDuration;
      if (maxDuration && (maxDuration < 60 || maxDuration > 3600)) {
        warnings.push('Max duration should be between 60 and 3600 seconds');
      }

      const turnTimeout = analysis.conversationSettings?.turnTimeout;
      if (turnTimeout && (turnTimeout < 5 || turnTimeout > 60)) {
        warnings.push('Turn timeout should be between 5 and 60 seconds');
      }

      const silenceTimeout = analysis.conversationSettings?.silenceTimeout;
      if (silenceTimeout && (silenceTimeout < 10 || silenceTimeout > 600)) {
        warnings.push('Silence timeout should be between 10 and 600 seconds');
      }

      // Speech speed validation
      if (analysis.speechSpeed !== undefined && analysis.speechSpeed !== null) {
        if (analysis.speechSpeed < 0.7 || analysis.speechSpeed > 1.2) {
          errors.push('Speech speed must be between 0.7 and 1.2');
        }
      }

      // Pronunciation validation
      if (analysis.pronunciations && analysis.pronunciations.length > 0) {
        const invalidPronunciations = analysis.pronunciations.filter(
          p => !p.word?.trim() || !p.pronunciation?.trim()
        );

        if (invalidPronunciations.length > 0) {
          errors.push('Some pronunciations are incomplete');
        }
      }
      break;

    case 'review':
      // Final validation - check all critical fields
      const brandingValidation = validateStep('branding', analysis);
      const identityValidation = validateStep('identity', analysis);
      const conversationValidation = validateStep('conversation', analysis);

      if (!brandingValidation.isValid) {
        errors.push('Please complete the Branding step');
      }

      if (!identityValidation.isValid) {
        errors.push('Please complete the Identity step');
      }

      if (!conversationValidation.isValid) {
        errors.push('Please complete the Conversation Flow step');
      }
      break;
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

export const isStepComplete = (stepId: string, analysis: SetupAnalysisResult): boolean => {
  const validation = validateStep(stepId, analysis);
  return validation.isValid;
};

export const getStepCompletionPercentage = (analysis: SetupAnalysisResult): number => {
  const steps = ['branding', 'identity', 'conversation', 'insights', 'knowledge', 'advanced'];
  const completedSteps = steps.filter(stepId => isStepComplete(stepId, analysis));
  return Math.round((completedSteps.length / steps.length) * 100);
};
